import {
  ErrorResponse,
  User,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
} from 'oidc-client-ts';

import {
  AuthErrorCode,
  CredentialSourceKind,
  SessionEndReason,
} from '../auth-core.const';
import { Credential, Unsubscribe } from '../auth-core.types';
import {
  credentialRejected,
  isCredentialRejected,
} from '../helpers/credential-rejected.helpers';
import { purgeLegacyOidcState } from '../helpers/legacy-state.helpers';
import { resolveWebStorage } from '../helpers/storage.helpers';
import { LoggerPort } from '../ports/ports.types';

import {
  CredentialSource,
  SignInOptions,
  SignOutOptions,
} from './credential-source.types';

export type OidcCredentialSourceOptions = {
  /**
   * Injected rather than constructed here, so the source can be driven by a
   * fake in tests. The current provider hard-wires `new UserManager(config)`
   * inside a hook, which is why none of its behaviour is covered.
   */
  userManager: UserManager;
  logger: LoggerPort;
};

/**
 * Wraps `oidc-client-ts` behind {@link CredentialSource}.
 *
 * The library stays an implementation detail: nothing it defines appears in
 * this module's exported types, so the engine can be swapped without touching
 * a consumer.
 */
export class OidcCredentialSource implements CredentialSource {
  readonly kind = CredentialSourceKind.Oidc;

  private readonly userManager: UserManager;
  private readonly logger: LoggerPort;
  private readonly listeners = new Set<
    (credential: Credential | null, reason?: SessionEndReason) => void
  >();
  private readonly detachers: Unsubscribe[] = [];

  private credential: Credential | null = null;

  constructor(options: OidcCredentialSourceOptions) {
    this.userManager = options.userManager;
    this.logger = options.logger;
    this.attachEvents();
  }

  async init(): Promise<Credential | null> {
    let user: User | null | undefined | void;

    if (hasAuthParams()) {
      user = await this.userManager.signinCallback();
      // The callback concludes the protocol exchange, so the response params
      // must not survive it. `oidc-client-ts` never touches the address bar,
      // and leaving them there means a later `init()` still sees
      // `hasAuthParams()` and re-runs the callback against a state entry that
      // has already been consumed.
      stripAuthParams();
    }

    // `signinCallback()` can resolve to `undefined` without having produced a
    // user of its own. Falling through to `getUser()` is what turns that into
    // the session that was just established, instead of reporting the tab as
    // signed out and bouncing it back to /login.
    user ??= await this.userManager.getUser();
    if (user?.expired && user.refresh_token) user = await this.renewStoredUser();

    this.setCredential(toCredential(user ?? null));
    return this.credential;
  }

  /**
   * A stored user whose access token lapsed while the tab was closed or asleep
   * can still be renewed with one request. Runs inside `init()`, before the
   * client starts scheduling, so it cannot race another renewal.
   */
  private async renewStoredUser(): Promise<User | null> {
    try {
      return await this.spendRefreshToken();
    } catch (error) {
      this.logger.info('Stored session could not be renewed on start-up', { error });
      if (isCredentialRejected(error)) await this.userManager.removeUser();
      return null;
    }
  }

  getCredential(): Credential | null {
    return this.credential;
  }

  /**
   * Renews by spending the refresh token — a single `POST /connect/token`.
   *
   * The refresh token is checked here rather than left to `signinSilent()`
   * because `UserManagerSettings` defaults `silent_redirect_uri` to
   * `redirect_uri`. With no refresh token the library therefore does not report a
   * missing silent URI: it opens a hidden iframe on the *interactive* callback
   * and boots the whole app inside it, which is the 10s silent hang CORE-2666
   * exists to remove. Renewal is the refresh token or an ordinary error.
   */
  async renew(): Promise<Credential | null> {
    const stored = await this.userManager.getUser();
    if (!stored?.refresh_token) {
      throw credentialRejected(
        'auth-core: no refresh token available to renew with. The session has to be ' +
          're-established interactively.',
      );
    }

    this.setCredential(toCredential(await this.spendRefreshToken()));
    return this.credential;
  }

  /**
   * An `ErrorResponse` here is the token endpoint answering — `invalid_grant`
   * for a spent or expired refresh token — so the chain is gone. Anything else
   * (a dropped connection, a timeout) is worth retrying.
   */
  private async spendRefreshToken(): Promise<User | null> {
    try {
      return await this.userManager.signinSilent();
    } catch (error) {
      if (error instanceof ErrorResponse) {
        throw credentialRejected(
          `auth-core: refresh token refused (${error.error})`,
          error,
        );
      }
      throw error;
    }
  }

  async revoke(): Promise<void> {
    await this.userManager.removeUser();
    this.setCredential(null);
  }

  subscribe(
    listener: (credential: Credential | null, reason?: SessionEndReason) => void,
  ): Unsubscribe {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * The expiry timers live on the `UserManager`, and the handlers that act on
   * them are ours. Nothing in the previous provider detached either, so unmounting
   * left a live renewal loop for the lifetime of the document.
   */
  dispose(): void {
    for (const detach of this.detachers.splice(0)) detach();
    this.listeners.clear();
  }

  /**
   * `returnUrl` is passed as the OIDC `state`, which `oidc-client-ts` persists
   * to the state store — `localStorage` by default. Only ever a string: the
   * current implementation passes the whole auth state object here, which puts
   * the live access, id and refresh tokens into `localStorage` on every logout.
   */
  async signIn(options: SignInOptions = {}): Promise<void> {
    await this.userManager.signinRedirect({
      state: options.returnUrl,
      extraQueryParams: options.extraQueryParams,
    });
  }

  async signOut(options: SignOutOptions = {}): Promise<void> {
    await this.userManager.signoutRedirect({ state: options.returnUrl });
  }

  private attachEvents(): void {
    const events = this.userManager.events;

    this.track(
      () => events.addUserLoaded(this.onUserLoaded),
      () => events.removeUserLoaded(this.onUserLoaded),
    );
    this.track(
      () => events.addUserUnloaded(this.onUserUnloaded),
      () => events.removeUserUnloaded(this.onUserUnloaded),
    );
    // `accessTokenExpiring` is deliberately **not** subscribed to. It fires once
    // per token and is re-armed only on `userLoaded`, so a single failed renewal
    // meant no scheduled attempt ever again. `AuthClient` polls instead, which
    // retries by construction and keeps renewal on one single-flighted path.
    this.track(
      () => events.addAccessTokenExpired(this.onAccessTokenExpired),
      () => events.removeAccessTokenExpired(this.onAccessTokenExpired),
    );
    // Registered *and* removed. The current provider adds this one inline and
    // omits it from cleanup, so every effect re-run leaks another handler.
    this.track(
      () => events.addUserSignedOut(this.onUserSignedOut),
      () => events.removeUserSignedOut(this.onUserSignedOut),
    );
  }

  private track(attach: () => void, detach: () => void): void {
    attach();
    this.detachers.push(detach);
  }

  private readonly onUserLoaded = (user: User): void => {
    this.setCredential(toCredential(user));
  };

  private readonly onUserUnloaded = (): void => {
    this.setCredential(null);
  };

  /**
   * An expired token must stop counting as authenticated. The current reducer
   * records expiry in a flag nobody reads and leaves `isAuthenticated` true, so
   * an app can render as signed in while every request 401s.
   */
  private readonly onAccessTokenExpired = (): void => {
    this.logger.warn('Access token expired', { code: AuthErrorCode.RenewFailed });
    this.setCredential(null, SessionEndReason.Expired);
  };

  private readonly onUserSignedOut = (): void => {
    this.logger.info('Identity provider reported the session ended');
    this.setCredential(null, SessionEndReason.ProviderSignedOut);
  };

  private setCredential(credential: Credential | null, reason?: SessionEndReason): void {
    this.credential = credential;
    for (const listener of [...this.listeners]) {
      listener(credential, reason);
    }
  }
}

function toCredential(user: User | null): Credential | null {
  if (!user?.access_token || user.expired) return null;

  return {
    accessToken: user.access_token,
    idToken: user.id_token,
    expiresAt: user.expires_at,
    subject: user.profile?.sub,
    claims: user.profile as Readonly<Record<string, unknown>>,
  };
}

/** Params an OIDC authorization response adds; none belong to the application. */
const AUTH_RESPONSE_PARAMS = [
  'code',
  'state',
  'session_state',
  'error',
  'error_description',
  'error_uri',
  'iss',
];

/** True when the current URL carries an OIDC redirect response. */
export function hasAuthParams(location: Location = window.location): boolean {
  const carriesResponse = (search: string) => {
    const params = new URLSearchParams(search);
    return (params.has('code') || params.has('error')) && params.has('state');
  };

  // `response_mode=query` first, then `fragment` — a provider may use either,
  // and only checking the query silently ignores a fragment response.
  return (
    carriesResponse(location.search) || carriesResponse(location.hash.replace('#', '?'))
  );
}

/**
 * Removes the OIDC response params from the address bar, leaving everything
 * else on the URL intact.
 */
export function stripAuthParams(): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  AUTH_RESPONSE_PARAMS.forEach((param) => url.searchParams.delete(param));

  const fragment = new URLSearchParams(url.hash.replace('#', '?'));
  AUTH_RESPONSE_PARAMS.forEach((param) => fragment.delete(param));
  const remaining = fragment.toString();
  const hash = remaining ? `#${remaining}` : '';

  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${hash}`);
}

/**
 * Rejects configurations that cannot renew.
 *
 * `offline_access` is what issues the refresh token, and since CORE-2666 the
 * refresh token is the only renewal path: `signinSilent()` spends it on a single
 * `POST /connect/token`. Without the scope there is no refresh token, and every
 * renewal then fails the guard in {@link OidcCredentialSource.renew} — the
 * session ends at the first token expiry.
 *
 * That is a configuration mistake with a symptom an hour away from its cause, so
 * it fails loudly at construction rather than mid-session.
 */
function assertRenewableConfig(settings: UserManagerSettings): void {
  const scopes = settings.scope?.split(/\s+/) ?? [];
  if (!scopes.includes('offline_access')) {
    throw new Error(
      'auth-core: scope must include offline_access. It issues the refresh token, ' +
        'which is the only renewal path — without it the session ends at the first ' +
        'token expiry.',
    );
  }

  // The library's `SilentRenewService` calls `signinSilent()` directly. That
  // bypasses the refresh-token guard in `renew()` and re-opens the hidden iframe
  // — aimed at `redirect_uri`, since `UserManagerSettings` defaults the silent
  // URI to it — and, worse, gives the page a second renewal loop racing
  // `AuthClient`'s. Two loops spend the same refresh token concurrently.
  if (settings.automaticSilentRenew) {
    throw new Error(
      'auth-core: automaticSilentRenew must not be set. auth-core schedules renewal ' +
        "itself; the library's version bypasses the refresh-token guard and falls " +
        'back to the hidden iframe.',
    );
  }
}

/**
 * Builds a `UserManager` with the storage tiers stated explicitly.
 *
 * The defaults put the user (with all three tokens) in `sessionStorage` but
 * protocol state in `localStorage`. Leaving that implicit is how the token leak
 * went unnoticed — nobody reading the config could see where anything landed.
 */
export function createOidcUserManager(
  settings: UserManagerSettings & { authority: string; client_id: string },
): UserManager {
  assertRenewableConfig(settings);

  // State used to land in `localStorage` (the library default) and only ever
  // left it when a matching callback ran, so abandoned flows piled up there
  // indefinitely. Pinning it to `sessionStorage` below stops the growth; this
  // clears what earlier builds already left behind. See FD-3069.
  purgeLegacyOidcState();

  const store = resolveWebStorage('session');

  return new UserManager({
    ...settings,
    userStore: settings.userStore ?? (store && new WebStorageStateStore({ store })),
    stateStore: settings.stateStore ?? (store && new WebStorageStateStore({ store })),
  });
}
